/**
 * The Scoring tab — yt-channel-scoring, the seventh pipeline: a feeder selects
 * channels from Postgres into a Mongo work queue, four calculators claim them,
 * fetch their features from ClickHouse and score them, and a supplier writes
 * the v2 scores back to `channel_info` — plus a CSV/API side (FastAPI + a
 * Celery worker) that scores uploaded lists.
 *
 * SIX SECTIONS, ONE SOURCE OF JUDGEMENT. Every panel renders the collector's
 * `/metrics/scoring/*` payloads (one query each, its own failure state), and
 * every alarm on this tab is a server FINDING: the collector computes them once
 * (mirrors-collector, collector/domain/scoring_health.py) for this tab, Triage,
 * the rail and the alert email alike, so the four can never disagree. The
 * board adds no threshold of its own; it lights the stage or figure a finding
 * names, and lists the finding beside it with its mechanism.
 *
 * FRESHNESS LEADS. Scoring v2 is re-scoring ~26M rows; the failure that would
 * hide is workers beating while nothing is scored or written. So "since
 * scored / written / fed" leads the health section, and every such figure says
 * how old its own reading is: these are seconds as of the payload's
 * `generated_at`, and the board holds a load for up to 24 h — a held "scored
 * 4 s ago" must never read as live.
 *
 * NULL IS "CANNOT STATE", everywhere: a key never written, a source the server
 * could not read, a rate with no baseline yet. It renders through the unstated
 * register (dim ink, dashed rule — DESIGN.md), never as 0 and never in a hue.
 * A rate with no baseline yet reads "measuring". A real 0 (an empty queue, zero
 * samples) is a number and is shown as one.
 *
 * NO CHANNEL HUE (DESIGN.md, the qdrant precedent): neutral edges and bars;
 * only the alarm budget colours a figure, and only when a finding says so.
 *
 * Finding ids are OPAQUE (`score_shift.<10K`, `role_dead.calculator`): they are
 * compared as strings to decide where a finding is shown, and never become a
 * DOM id, a selector or a URL.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
  useScoringFailures, useScoringJobs, useScoringOutcomes, useScoringOverview, useScoringProgress,
} from '../api/queries'
import type {
  ScoringFailures as FailuresPayload, ScoringFinding, ScoringJob, ScoringJobs as JobsPayload,
  ScoringLatency, ScoringOutcomes as OutcomesPayload, ScoringOverview, ScoringRole,
  ScoringThroughputEntry,
} from '../api/schemas'
import type { RailEntry } from '../components/PipelineTabs'
import { DayBars } from '../components/charts'
import { Bar, PanelState, Pill, Stat, Tile } from '../components/ui'
import { age, headline, n, pct } from '../components/format'
import { Unstated } from './Strips'
import { scoringReads } from './scoringReads'
import type { Read } from './scoringReads'
// The flow row is drawn in FlowBoard's visual language (its .fb-* classes), and
// a finding's severity mark is the board's one tier vocabulary (.tier-* in
// Strips.css: square / triangle / hollow circle). Imported, not copied.
import './FlowBoard.css'
import './Strips.css'
import './Scoring.css'

/* ----------------------------------------------------------------- helpers */

const CANNOT = 'cannot state'

/** The cannot-state register. `—` in a figure's place, words where there is room. */
function Nil({ children = '—' }: { children?: ReactNode }) {
  return <Unstated>{children}</Unstated>
}

/** A headline figure, exact on hover once headline() rounds it past 1e6. */
const Big = ({ v }: { v: number }) => <span title={n(v)}>{headline(v)}</span>

/** Milliseconds as an operator reads a batch: 5,755 ms is "5.8s", 94,565 is "1.6m". */
export function dur(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60_000).toFixed(1)}m`
}

const rate = (perS: number) => `${perS.toFixed(2)}/s`

/* ---------------------------------------------------------- score versions */

/** The supplier's counters bucket rows written with no version under `none`; an
 *  empty name is the same absence, and "v" alone is not a version either. */
const isNoVersion = (v: string) => v.trim() === '' || v.trim().toLowerCase() === 'none'

/** A score_version as the team writes it: `2.0.0` → "v2.0.0". The `none`
 *  bucket is not a version, and "vnone" is not a word. */
const versionWord = (v: string): string => (isNoVersion(v) ? 'no version' : `v${v}`)

/**
 * The version a re-score is heading for, from the data and never typed in: the
 * scoring API's own `info.score_version`, else the only version the supplier has
 * written since telemetry went live. null when neither names one (or the writes
 * name several) — then a label says what it knows and no version.
 */
function targetVersion(
  info: string | null | undefined, written: Record<string, number> | null | undefined,
): string | null {
  if (info && !isNoVersion(info)) return info
  const named = Object.keys(written ?? {}).filter((v) => !isNoVersion(v))
  return named.length === 1 ? named[0]! : null
}

/**
 * "as of 09:30Z · 3m ago" — the age of a MEASUREMENT, not of the figure in it.
 *
 * The absolute stamp is the anchor (it stays true however long the board sits
 * untouched); the relative age is the convenience, kept current by <AsOf>.
 * The date is added once the reading is from another UTC day, which a 24 h
 * hold makes routine.
 */
export function asOf(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return `as of ${iso}`
  const at = new Date(t).toISOString()
  const sameDay = at.slice(0, 10) === new Date(now).toISOString().slice(0, 10)
  const stamp = `${sameDay ? '' : `${at.slice(5, 10)} `}${at.slice(11, 16)}Z`
  // A reading "from the future" is clock skew between two machines, not news.
  return `as of ${stamp} · ${age(Math.max(0, (now - t) / 1000))} ago`
}

/** Re-renders its caller every 30 s. Nothing is fetched — only the "N ago"
 *  beside a held reading moves, so a wall display left alone for hours does
 *  not keep claiming "3m ago". */
function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(t)
  }, [everyMs])
  return now
}

function AsOf({ iso, prefix = '' }: { iso: string; prefix?: string }) {
  const now = useNow()
  return <>{prefix}{asOf(iso, now)}</>
}

/* ---------------------------------------------------------------- findings */

type Sev = ScoringFinding['sev']
type Alarm = 'crit' | 'warn'

const SEV_RANK: Record<Sev, number> = { crit: 2, warn: 1, info: 0 }
/** DESIGN.md's alarm tiers: crisis a filled square, warning a filled triangle,
 *  advisory a hollow circle. `info` is reported, not paged — the advisory tier. */
const SEV_TIER: Record<Sev, string> = { crit: 'crisis', warn: 'warning', info: 'advisory' }

/** The worst alarm among `fs`. Info never raises anything. */
function worst(fs: readonly ScoringFinding[]): Alarm | null {
  let w: Alarm | null = null
  for (const f of fs) {
    if (f.sev === 'crit') return 'crit'
    if (f.sev === 'warn') w = 'warn'
  }
  return w
}

/** The worst alarm among the findings with exactly these ids. */
const sevOf = (fs: readonly ScoringFinding[], ...ids: string[]): Alarm | null =>
  worst(fs.filter((f) => ids.includes(f.id)))

/** The alarm a figure may wear: only a figure the server STATED. A null cell
 *  is in the cannot-state register (dim ink, dashed rule) and carries no
 *  hue and no mark, whatever finding names its column — it claims no severity
 *  the server never gave it. The finding is still listed beside it. */
const whenStated = (v: unknown, s: Alarm | null): Alarm | null => (v == null ? null : s)

/** A figure's colour class when a finding names it. Never alone: <Mark>
 *  goes inside the same figure. */
const lit = (s: Alarm | null) => (s ? `sc-${s}` : '')

/** The shape that rides with every lit figure — DESIGN.md's tier marks, a
 *  filled triangle (warning) or square (crisis) — carrying its word for
 *  assistive tech. An empty element, so the figure's own text is unchanged;
 *  the finding that lit it is listed in the same section. */
function Mark({ sev }: { sev: Alarm | null | undefined }) {
  return sev
    ? <span className={`sc-mark sc-mark-${sev}`} role="img" aria-label={sev === 'crit' ? 'crisis' : 'warning'} />
    : null
}

/** A source the collector could not read, in the tier the server rated it: the
 *  tier's shape and word beside the plain sentence — never the crisis-red
 *  `.panel-err` that a failed read of the panel itself wears. */
function BlindNote({ sev, children }: { sev: Alarm; children: ReactNode }) {
  return (
    <div className="sc-blind">
      <span className={`tier tier-${SEV_TIER[sev]}`}>
        <span className="tier-mark" aria-hidden="true" />
        <span className="tier-txt"><b>{sev}</b></span>
      </span>
      <p>{children}</p>
    </div>
  )
}

/** One server finding: severity as shape AND word, the title, the mechanism,
 *  and the number that makes it real. Worst first; server order within a
 *  severity. Keys combine index and id — an opaque id is a fine React key, and
 *  this way two findings sharing an id still render. */
export function Findings({ items, label }: { items: readonly ScoringFinding[]; label: string }) {
  if (!items.length) return null
  const sorted = [...items].sort((a, b) => SEV_RANK[b.sev] - SEV_RANK[a.sev])
  return (
    <ul className="sc-finds" aria-label={label}>
      {sorted.map((f, i) => (
        <li key={`${i}:${f.id}`} className={`sc-find sc-find-${f.sev}`} data-sev={f.sev}>
          <span className={`tier tier-${SEV_TIER[f.sev]}`}>
            <span className="tier-mark" aria-hidden="true" />
            <span className="tier-txt"><b>{f.sev}</b></span>
          </span>
          <span className="sc-find-txt">
            <b>{f.title}</b>
            <span>{f.why}</span>
          </span>
          {f.value ? <span className="sc-find-v tnum">{f.value}</span> : null}
        </li>
      ))}
    </ul>
  )
}

/* ------------------------------------------------------------ 1. the flow */

type StageKey = 'feeder' | 'queue' | 'calculators' | 'supplier' | 'sink' | 'clickhouse'
/** `held`: a queue holding work. A queue has no rate of its own, so it never
 *  claims "flowing" — that word needs a measured completed-work rate.
 *  `unreadable`: the collector could not read the stage's source
 *  (`source_unreadable.*`). The warning tier, because blind is not healthy — but
 *  not "behind", which says work is late when nothing about it is known. */
type Status = 'flowing' | 'behind' | 'blocked' | 'idle' | 'held' | 'unknown' | 'unreadable'

/** Role name in `/overview.roles` (and `role_*.{role}` ids) → its stage. */
const ROLE_STAGE: Record<string, StageKey> = {
  feeder: 'feeder', calculator: 'calculators', supplier: 'supplier',
}

/** The /overview findings that belong to the API & jobs section, not the flow. */
const API_FINDINGS = ['api_down', 'celery_backlog']

/**
 * Which stage a finding names (spec §6.1), or null. Unknown ids are not
 * dropped: they light nothing but are still listed under the flow.
 */
export function stageOf(id: string): StageKey | null {
  const dot = id.indexOf('.')
  const head = dot < 0 ? id : id.slice(0, dot)
  const tail = dot < 0 ? '' : id.slice(dot + 1)
  switch (head) {
    case 'role_dead': case 'role_partial': return ROLE_STAGE[tail] ?? null
    case 'feeder_not_feeding': case 'feed_query_slow': return 'feeder'
    // stale claims nobody is taking back; an unreadable queue is a blind stage
    case 'claims_orphaned': return 'queue'
    case 'source_unreadable': return tail === 'queue' ? 'queue' : null
    // calculators dying mid-batch, slow batches, failing channels, no progress;
    // calculator_failing is the same stage's other no-progress shape — running,
    // claiming, and every whole batch failing (the claim slot frees at once, so
    // the queue keeps moving and calculator_stalled cannot see it)
    case 'calculator_stalled': case 'calculator_failing': case 'batch_slow':
    case 'claims_reclaimed': case 'failure_rate':
      return 'calculators'
    case 'sink_stale': return 'sink'
    default: return null
  }
}

/** A crit finding blocks the stage it names, a warn puts it behind. */
const litStatus = (base: Status, sev: Alarm | null): Status =>
  sev === 'crit' ? 'blocked' : sev === 'warn' ? 'behind' : base

const roleOf = (o: ScoringOverview, name: string): ScoringRole | undefined =>
  o.roles.find((r) => r.role === name)
const tpOf = (o: ScoringOverview, name: string): ScoringThroughputEntry | undefined =>
  o.throughput[`scoring-${name}`]

/** A role stage before any finding lights it. A never-beaten role, or a rate
 *  with no baseline, cannot be stated; a measured 0/s is merely idle — the
 *  server, not the board, decides when "idle" is a stall. */
function roleBase(r: ScoringRole | undefined, t: ScoringThroughputEntry | undefined):
  { status: Status; word: string } {
  if (!r || r.alive === null) return { status: 'unknown', word: CANNOT }
  if (r.alive === 0) return { status: 'idle', word: 'idle' }
  if (!t || t.per_s === null) return { status: 'unknown', word: 'measuring' }
  return t.per_s > 0 ? { status: 'flowing', word: 'flowing' } : { status: 'idle', word: 'idle' }
}

/** "3/4 live", with a never-beaten count in the cannot-state register. */
function Live({ r }: { r: ScoringRole | undefined }) {
  if (!r) return <Nil />
  return <>{r.alive === null ? <Nil /> : r.alive}/{r.expected} live</>
}

/** How long the oldest doc in a status has waited, through the board's age().
 *  A null age is "cannot state" — unless the count itself says there is
 *  nothing to age (`none`), which is a fact, not a gap. A stated age always
 *  wins: it is the measurement. */
function Oldest({ what, s, none }: { what: string; s: number | null; none: string | null }) {
  if (s !== null) return <>oldest {what} {age(s)}</>
  if (none !== null) return <>{none}</>
  return <>oldest {what} <Nil>{CANNOT}</Nil></>
}

/**
 * The feeder's feed-query line, lit by `feed_query_slow` — BOTH figures it reads.
 * The finding fires on the latest single query (`last_query_ms`) OR on a query in
 * flight (a live feeder in phase `querying` whose `updated_s` is over the line),
 * and the board adds no threshold of its own, so it cannot tell which branch
 * fired. It shows both, and the finding lights the line that holds them rather
 * than picking one and lighting a figure the rule did not judge. This is not the
 * latency ring's median, which the finding no longer looks at.
 *
 * "Running" is shown only for a LIVE feeder (the collector's rule): a dead feeder
 * leaves its last phase behind, and a stale `querying` is not a query in flight.
 * A feeder whose liveness cannot be stated is not shown as running either.
 *
 * The line is lit when a figure on it is stated. A figure the server could not
 * state is in the cannot-state register; with neither stated the whole line is,
 * and wears no mark whatever the finding says.
 */
function FeedQuery({ f, live, sev }: {
  f: ScoringOverview['feeder']; live: boolean; sev: Alarm | null
}) {
  const last = f.last_query_ms
  const running = live && f.phase === 'querying' ? f.updated_s : null
  const s = whenStated(last ?? running, sev)
  return (
    <span className={lit(s)}>
      <Mark sev={s} />last query {last === null ? <Nil>{CANNOT}</Nil> : dur(last)}
      {running === null ? null : <> · running {age(running)}</>}
    </span>
  )
}

interface StageView {
  key: StageKey
  kind: string
  label: string
  metric: ReactNode
  sub: ReactNode
  /** A second line of its own, where one more figure would be elided from the
   *  first (`.fb-sub` clamps to two lines). */
  sub2?: ReactNode
  status: Status
  word: string
}

function stages(o: ScoringOverview): StageView[] {
  const fs = o.findings
  const word = (s: Status, base: string) =>
    s === 'blocked' ? 'blocked' : s === 'behind' ? 'behind' : s === 'unreadable' ? 'unreadable' : base

  const feeder = roleOf(o, 'feeder')
  const calc = roleOf(o, 'calculator')
  const supplier = roleOf(o, 'supplier')
  const ftp = tpOf(o, 'feeder'); const ctp = tpOf(o, 'calculator'); const stp = tpOf(o, 'supplier')
  const q = o.queue
  const fetch: ScoringLatency = o.latency.fetch
  const fBase = roleBase(feeder, ftp)
  // A feeder that has walked every tier is done, not stuck.
  const feederBase = feeder?.alive != null && o.feeder.phase === 'exhausted'
    ? { status: 'idle' as Status, word: 'exhausted' } : fBase
  // LIVENESS AND CONTENTS LIE; MEASURED RATES DO NOT. A queue holding items,
  // a sink with a recent stamp and a non-empty latency ring all read normal
  // while nothing moves, so none of them may claim "flowing" on its own:
  //  - the queue has no rate, so it says what it is doing — holding, or empty;
  //  - channel_info moves at the supplier's measured write rate;
  //  - ClickHouse answers at the calculators' measured scoring rate.
  const queueBase: { status: Status; word: string } =
    q.error !== null || q.pending === null ? { status: 'unknown', word: CANNOT }
      : (q.pending + (q.inprogress ?? 0) + (q.done ?? 0)) > 0
        ? { status: 'held', word: 'holding' } : { status: 'idle', word: 'empty' }
  const sinkBase = roleBase(supplier, stp)
  const chBase = roleBase(calc, ctp)

  const mk = (key: StageKey, base: { status: Status; word: string },
              v: Omit<StageView, 'key' | 'status' | 'word'>): StageView => {
    const named = fs.filter((f) => stageOf(f.id) === key)
    const sev = worst(named)
    // BLIND IS NOT LATE. A stage whose figures cannot be stated and whose only
    // warning is the collector's `source_unreadable.*` is lit because its source
    // could not be read, not because work is waiting: it keeps the warning tier
    // and says so, rather than borrowing "behind" (a crit still blocks).
    const blind = base.status === 'unknown' && sev === 'warn'
      && named.filter((f) => f.sev === 'warn').every((f) => f.id.startsWith('source_unreadable.'))
    const status: Status = blind ? 'unreadable' : litStatus(base.status, sev)
    return { key, status, word: word(status, base.word), ...v }
  }

  const rateMetric = (r: ScoringRole | undefined, t: ScoringThroughputEntry | undefined, what: string) => (
    <>
      {t?.per_s == null
        ? <Nil>{r?.alive == null ? '—' : 'measuring'}</Nil>
        : rate(t.per_s)}
      <em> {what}</em>
    </>
  )

  return [
    mk('feeder', feederBase, {
      kind: 'producer', label: 'Feeder',
      metric: <>{o.freshness.fed_s === null ? <Nil /> : age(o.freshness.fed_s)}<em> since fed</em></>,
      sub: <>{o.feeder.phase ?? <Nil />} · {o.feeder.tier ?? <Nil />} · <Live r={feeder} /></>,
      // the figures feed_query_slow judges: the stage is lit by that finding, and
      // these are the numbers that lit it
      sub2: <FeedQuery f={o.feeder} live={(feeder?.alive ?? 0) > 0} sev={sevOf(fs, 'feed_query_slow')} />,
    }),
    mk('queue', queueBase, {
      kind: 'Mongo queue', label: 'Queue',
      metric: <>{q.pending === null ? <Nil /> : headline(q.pending)}<em> pending</em></>,
      sub: q.error !== null ? <Nil>{q.error}</Nil> : (
        <>
          {q.inprogress === null ? <Nil /> : n(q.inprogress)} in progress
          {' · '}
          {q.oldest_inprogress_s !== null
            ? `oldest claim ${age(q.oldest_inprogress_s)}`
            // no claim held is a fact; claims held with no stamp is not
            : q.inprogress === 0 ? 'no claim held' : <>oldest claim <Nil /></>}
        </>
      ),
      // how long queued work has waited: the figure calculator_stalled judges
      sub2: <Oldest what="pending" s={q.oldest_pending_s} none={q.pending === 0 ? 'none pending' : null} />,
    }),
    mk('calculators', roleBase(calc, ctp), {
      kind: 'worker', label: 'Calculators',
      metric: rateMetric(calc, ctp, 'scored'),
      sub: <>
        <Live r={calc} /> · batch p50 {o.latency.batch.p50 === null ? <Nil /> : dur(o.latency.batch.p50)}
      </>,
    }),
    mk('supplier', roleBase(supplier, stp), {
      kind: 'worker', label: 'Supplier',
      metric: rateMetric(supplier, stp, 'written'),
      sub: <><Live r={supplier} /> · {q.done === null ? <Nil /> : n(q.done)} done waiting</>,
      // how long scored work has waited for it: the figure sink_stale judges
      sub2: <Oldest what="done" s={q.oldest_done_s} none={q.done === 0 ? 'none waiting' : null} />,
    }),
    mk('sink', sinkBase, {
      kind: 'sink', label: 'channel_info',
      metric: <>{o.freshness.written_s === null ? <Nil /> : age(o.freshness.written_s)}<em> since written</em></>,
      sub: (() => {
        const v = targetVersion(o.info.score_version, null)
        return `Postgres · ${v ? `${versionWord(v)} scores` : 'scores'} land here`
      })(),
    }),
    mk('clickhouse', chBase, {
      kind: 'upstream · calculators fetch', label: 'ClickHouse',
      metric: <>{fetch.p50 === null ? <Nil /> : dur(fetch.p50)}<em> fetch p50</em></>,
      sub: <>p99 {fetch.p99 === null ? <Nil /> : dur(fetch.p99)} · {n(fetch.samples)} samples</>,
    }),
  ]
}

const STATUS_TIER: Record<Status, string> = {
  flowing: 'clear', behind: 'warning', blocked: 'crisis', idle: 'clear', held: 'clear', unknown: 'clear',
  unreadable: 'warning',
}

function StageNode({ s }: { s: StageView }) {
  return (
    <li className={`sc-stage${s.key === 'sink' ? ' sc-end' : ''}${s.key === 'clickhouse' ? ' sc-up' : ''}`}
        data-stage={s.key} data-status={s.status}>
      <div className="fb-node sc-node" data-kind={s.kind} data-status={s.status}>
        <span className="fb-kind">{s.kind}</span>
        <span className="fb-label">{s.label}</span>
        <span className="fb-metric">{s.metric}</span>
        <span className="fb-sub">{s.sub}</span>
        {s.sub2 ? <span className="fb-sub">{s.sub2}</span> : null}
        {/* The status in words and shape, not only the edge colour. */}
        {s.status === 'unknown' ? (
          <span className="sc-st"><Nil>{s.word}</Nil></span>
        ) : (
          <span className={`sc-st tier tier-${STATUS_TIER[s.status]}`}>
            <span className="tier-mark" aria-hidden="true" />
            <span className="tier-txt"><b>{s.word}</b></span>
          </span>
        )}
      </div>
    </li>
  )
}

/**
 * Pipeline flow — the stages as a fixed row, lit by the findings that name
 * them. Fixed rather than served (unlike the collector's /topology): scoring's
 * shape is a constant of the service, and the payload names its stages.
 */
export function ScoringFlow() {
  const q = useScoringOverview()
  const o = q.data
  return (
    <Tile title="Scoring flow" span hint={o ? <AsOf iso={o.generated_at} /> : undefined}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {o ? (
          <div className="fb">
            <div className="fb-legend">
              <span className="fb-lg"><i data-s="flowing" />flowing</span>
              <span className="fb-lg"><i data-s="behind" />behind</span>
              <span className="fb-lg"><i data-s="blocked" />blocked</span>
              <span className="fb-lg"><i data-s="idle" />idle</span>
              {/* The queue's own mark (the clear tier's thin dash), not a lookalike. */}
              <span className="fb-lg">
                <span className="tier tier-clear"><span className="tier-mark" aria-hidden="true" /></span>holding
              </span>
              {/* A source the collector could not read: the warning tier with a
                  dashed edge (Scoring.css), so the swatch is dashed amber. */}
              <span className="fb-lg"><i data-s="unreadable" />unreadable</span>
              <span className="fb-lg"><i data-s="unknown" />{CANNOT}</span>
            </div>
            <ol className="sc-flow" aria-label="scoring stages, feeder to channel_info">
              {stages(o).map((s) => <StageNode key={s.key} s={s} />)}
            </ol>
            <Findings label="findings on the scoring flow"
                      items={o.findings.filter((f) => !API_FINDINGS.includes(f.id))} />
            <p className="fb-note">
              Rates are measured against the server’s one shared baseline sample —
              its previous read of each completed-work counter, by anyone: every
              reader of /overview resets it, the alerter every 10 min included —
              so the span it covers is the server’s, not the one between your loads,
              and it is never live.
              <b> “measuring”</b> means one sample exists so far; it is not zero.
              Only a measured completed-work rate says “flowing” — the queue only
              holds, channel_info moves at the supplier’s rate and ClickHouse at
              the calculators’. A stage is lit only by a finding the collector raised.
            </p>
          </div>
        ) : null}
      </PanelState>
    </Tile>
  )
}

/* -------------------------------------------------- 2. health & throughput */

/** A freshness figure: the age, or the cannot-state register. */
function Fresh({ label, s, sev }: { label: string; s: number | null; sev: Alarm | null }) {
  return s === null
    ? <Stat label={label} value={<Nil />} sub={<Nil>{CANNOT}</Nil>} />
    // the printed age is floored ("21m"); the hover keeps the exact seconds
    : <Stat label={label} value={<><Mark sev={sev} />{`${age(s)} ago`}</>} title={`${n(s)} s ago`}
            state={sev ?? undefined} />
}

/** A count from today's counters, or the cannot-state register. */
function Count({ label, v, sev, sub }: {
  label: string; v: number | null; sev?: Alarm | null; sub?: ReactNode
}) {
  return v === null
    ? <Stat label={label} value={<Nil />} sub={<Nil>{CANNOT}</Nil>} />
    : <Stat label={label} value={<><Mark sev={sev} /><Big v={v} /></>} title={n(v)}
            state={sev ?? undefined} sub={sub} />
}

const ROLES: Array<[string, string]> = [
  ['feeder', 'feeder'], ['calculator', 'calculators'], ['supplier', 'supplier'],
]

/** Health & throughput: freshness leads, then today's counters, the per-role
 *  rate table and the latency rings. */
export function ScoringHealth() {
  const q = useScoringOverview()
  const o = q.data
  const fs = o?.findings ?? []
  const asof = o ? <AsOf iso={o.generated_at} /> : undefined
  // The findings that light this section's figures are listed beside them,
  // not only under the flow: a lit figure always has its mechanism in view.
  const only = (...ids: string[]) => fs.filter((f) => ids.includes(f.id))
  const roleFindings = fs.filter((f) => f.id.startsWith('role_dead.') || f.id.startsWith('role_partial.'))
  return (
    <>
      <Tile title="Freshness" hint={asof}>
        <PanelState isLoading={q.isLoading} error={q.error}>
          {o ? (
            <>
              {o.redis_error !== null ? (
                // The server rates an unreadable Redis (source_unreadable.redis)
                // warn: blind, not healthy, but not a crisis. It is told in that
                // tier's mark and word — .panel-err's crisis red is for this
                // panel's own read failing, and would say "crisis" about a warning.
                <BlindNote sev={sevOf(fs, 'source_unreadable.redis') ?? 'warn'}>
                  Scoring telemetry could not be read — {o.redis_error}. Every figure
                  that depends on it cannot be stated.
                </BlindNote>
              ) : null}
              <div className="stats-row">
                <Fresh label="since scored" s={o.freshness.scored_s}
                       sev={sevOf(fs, 'calculator_stalled', 'calculator_failing')} />
                <Fresh label="since written" s={o.freshness.written_s} sev={sevOf(fs, 'sink_stale')} />
                <Fresh label="since fed" s={o.freshness.fed_s} sev={sevOf(fs, 'feeder_not_feeding')} />
              </div>
              <Findings label="freshness findings"
                        items={only('calculator_stalled', 'calculator_failing', 'sink_stale', 'feeder_not_feeding')} />
            </>
          ) : null}
        </PanelState>
      </Tile>

      {/* As-of here too: a load held across 00:00 UTC would otherwise show
          yesterday's counters under "Today". */}
      <Tile title="Today (UTC)"
            hint={o ? <>resets 00:00 UTC, so it falls to nothing when scoring stops · {asof}</> : undefined}>
        <PanelState isLoading={q.isLoading} error={q.error}>
          {o ? (
            <>
            <div className="stats-row">
              <Count label="scored" v={o.today.scored} />
              <Count label="failed" v={o.today.failed} sev={sevOf(fs, 'failure_rate')}
                     sub={o.today.failed !== null && o.today.scored !== null && o.today.failed + o.today.scored > 0
                       ? `${pct((100 * o.today.failed) / (o.today.failed + o.today.scored), 2)} of attempts`
                       : undefined} />
              <Count label="claims reclaimed" v={o.today.reclaimed} sev={sevOf(fs, 'claims_reclaimed')} />
            </div>
            <Findings label="today's findings" items={only('failure_rate', 'claims_reclaimed')} />
            </>
          ) : null}
        </PanelState>
      </Tile>

      <Tile title="Throughput" hint={o ? <>completed work since the server’s last sample · {asof}</> : undefined}>
        <PanelState isLoading={q.isLoading} error={q.error}>
          {o ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="t sc-t">
                <thead>
                  {/* Completed work first, liveness after it: a role can beat
                      and complete nothing (PRODUCT.md — liveness lies). */}
                  <tr>
                    <th>role</th><th className="n">rate</th><th className="n">per min</th>
                    <th className="n">completed</th><th className="n">live</th><th className="n">last beat</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLES.map(([name, label]) => {
                    const r = roleOf(o, name)
                    const t = tpOf(o, name)
                    const beats = (r?.instances ?? []).map((i) => i.beat_s)
                      .filter((b): b is number => b !== null)
                    const liveSev = whenStated(r?.alive, sevOf(fs, `role_dead.${name}`, `role_partial.${name}`))
                    const rateSev = whenStated(
                      t?.per_s, name === 'calculator' ? sevOf(fs, 'calculator_stalled', 'calculator_failing') : null)
                    return (
                      <tr key={name}>
                        <td>{label}</td>
                        <td className={`n ${lit(rateSev)}`}>
                          <Mark sev={rateSev} />
                          {t?.per_s == null ? <Nil>measuring</Nil> : rate(t.per_s)}
                        </td>
                        <td className="n q">{t?.per_min == null ? <Nil /> : n(Math.round(t.per_min))}</td>
                        <td className="n q">{t?.total == null ? <Nil /> : n(t.total)}</td>
                        <td className={`n ${lit(liveSev)}`}>
                          <Mark sev={liveSev} />
                          {!r || r.alive === null ? <Nil /> : `${r.alive}/${r.expected}`}
                        </td>
                        <td className="n q">{beats.length ? age(Math.min(...beats)) : <Nil />}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <Findings label="liveness findings" items={roleFindings} />
            </div>
          ) : null}
        </PanelState>
      </Tile>

      <Tile title="Latency" hint={o ? <>newest 200 per ring · {asof}</> : undefined}>
        <PanelState isLoading={q.isLoading} error={q.error}>
          {o ? (
            <>
            <table className="t sc-t">
              <thead>
                <tr><th>ring</th><th className="n">p50</th><th className="n">p99</th><th className="n">samples</th></tr>
              </thead>
              <tbody>
                {([
                  ['batch', o.latency.batch, null, sevOf(fs, 'batch_slow')],
                  ['ClickHouse fetch', o.latency.fetch, null, null],
                  // feed_query_slow is listed below but lights none of this ring: it
                  // judges the feeder's own query figures, which the feeder stage shows
                  ['feed query', o.latency.feed_query, null, null],
                ] as Array<[string, ScoringLatency, Alarm | null, Alarm | null]>).map(([label, l, p50Lit, p99Lit]) => {
                  const p50Sev = whenStated(l.p50, p50Lit)
                  const p99Sev = whenStated(l.p99, p99Lit)
                  return (
                    <tr key={label}>
                      <td>{label}</td>
                      <td className={`n ${lit(p50Sev)}`}><Mark sev={p50Sev} />{l.p50 === null ? <Nil /> : dur(l.p50)}</td>
                      <td className={`n ${lit(p99Sev)}`}><Mark sev={p99Sev} />{l.p99 === null ? <Nil /> : dur(l.p99)}</td>
                      <td className="n q">{n(l.samples)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <Findings label="latency findings" items={only('batch_slow', 'feed_query_slow')} />
            </>
          ) : null}
        </PanelState>
      </Tile>
    </>
  )
}

/* ---------------------------------------------------- 3. re-score progress */

/** The v2 re-score: how far, how much is left (a planner estimate, with the
 *  age of its statistics), where the feeder is, and when it could finish. */
export function ScoringProgress() {
  const q = useScoringProgress()
  const p = q.data
  const versions = p?.written ? Object.entries(p.written) : []
  // The re-score's target version comes from the data (the scoring API's own
  // version, from the overview this tab already holds), not from a label.
  const ov = useScoringOverview()
  const target = targetVersion(ov.data?.info.score_version, p?.written)
  const counted = p?.pending_exact === true
  const rescored = `re-scored${target ? ` as ${versionWord(target)}` : ''}${counted ? '' : ' (estimate)'}`
  return (
    <Tile title="Re-score progress" span
          hint={p ? <>{p.cached ? `server cache ${age(p.cache_age_s)} old · ` : 'read live · '}
            <AsOf iso={p.generated_at} /></> : undefined}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {p ? (
          <>
            {p.error !== null ? (
              <p className="panel-err" style={{ marginBottom: 10 }}>
                A source could not be read — {p.error}. Figures that depend on it cannot be stated.
              </p>
            ) : null}
            <div className="sc-progress">
              {p.pct_scored_est === null ? (
                <Stat label={rescored} value={<Nil />} sub={<Nil>{CANNOT}</Nil>} />
              ) : (
                <>
                  <Stat label={rescored} value={pct(p.pct_scored_est)}
                        sub={<>of ≈ {p.table_rows_est === null ? <Nil /> : <Big v={p.table_rows_est} />} channel_info
                          rows · {counted ? 'remainder counted from the index' : 'planner estimate, never a scan'}</>} />
                  {/* Neutral, not a channel hue: progress is data, not state. */}
                  <Bar pct={p.pct_scored_est} state="idle" />
                </>
              )}
            </div>
            <div className="stats-row">
              <Stat label={target ? `awaiting a ${versionWord(target)} score` : 'awaiting a re-score'} value={p.pending_est === null ? <Nil /> : <Big v={p.pending_est} />}
                    sub={counted ? 'counted, not estimated' : p.stats_as_of
                      ? <AsOf iso={p.stats_as_of} prefix="planner stats " />
                      : <Nil>stats age {CANNOT}</Nil>} />
              <Stat label="feeder tier" value={p.feeder.tier ?? <Nil />}
                    sub={p.feeder.phase ?? <Nil>phase {CANNOT}</Nil>} />
              <Stat label="ETA" value={p.eta_s === null ? <Nil /> : `≈ ${age(p.eta_s)}`}
                    sub={p.eta_s === null ? <Nil>{CANNOT}</Nil>
                      // a stated ETA keeps its basis even when the pace figure is absent
                      : `${p.eta_rate_per_s !== null ? `at ${rate(p.eta_rate_per_s)} · ` : ''}${p.eta_basis ?? 'recent'}’s pace`} />
              {p.written === null ? (
                <Stat label="written" value={<Nil />} sub={<Nil>{CANNOT}</Nil>} />
              ) : versions.length === 0 ? (
                <Stat label="written" value="0" sub="nothing written as any version yet" />
              ) : versions.map(([v, c]) => (
                <Stat key={v} label={isNoVersion(v) ? 'written with no version' : `written as ${versionWord(v)}`}
                      value={<Big v={c} />} sub="supplier counter" />
              ))}
            </div>
          </>
        ) : null}
      </PanelState>
    </Tile>
  )
}

/* ------------------------------------------------------------- 4. outcomes */

type Fields = Record<string, number>

/** Every `{prefix}{name}` key at the TOP level of a hash (tier-scoped keys
 *  start with `tier:` and are excluded) — whatever families are present, never
 *  a fixed list: the backend already sends gates the spec never named. */
function family(fields: Fields, prefix: string): Array<[string, number]> {
  return Object.entries(fields)
    .filter(([k]) => k.startsWith(prefix))
    .map(([k, v]) => [k.slice(prefix.length), v])
}

/** Known names first in their natural order, anything new after, largest first. */
function ordered(rows: Array<[string, number]>, known: readonly string[]): Array<[string, number]> {
  const rank = (k: string) => { const i = known.indexOf(k); return i < 0 ? known.length : i }
  return [...rows].sort((a, b) => rank(a[0]) - rank(b[0]) || b[1] - a[1])
}

/** The window the collector's outcome rules judge (scoring_health.outcome_findings):
 *  the current UTC day's hash, widened to today + yesterday while today holds
 *  fewer than 500 channels. Not "the last 24 h". */
const JUDGED = 'today (UTC), or today + yesterday early in the day'

const BANDS = ['High-performing', 'Good but monitor', 'Average / test only', 'Weak', 'Avoid']
const GATES = ['Active', 'Dormant', 'Insufficient Data', 'No Long-Form']
/** The gates that force a channel's score to 0; the other gates keep its score. */
const HARD_GATES = ['Insufficient Data', 'No Long-Form', 'Dormant'] as const
const CAPPED: Record<string, string> = { view: 'view ceiling', recency: 'recency ceiling' }

/** A subscriber tier's lower bound, for largest-first ordering: `10M+` → 1e7,
 *  `100K-1M` → 1e5, `<10K` → 0; unparseable names sort last. */
function lowerBound(tier: string): number {
  if (tier.startsWith('<')) return 0
  const m = /^([\d.]+)\s*([KMB]?)/i.exec(tier)
  if (!m) return -1
  const mult: Record<string, number> = { '': 1, K: 1e3, M: 1e6, B: 1e9 }
  return parseFloat(m[1]!) * (mult[m[2]!.toUpperCase()] ?? 1)
}

/** Tiers present in the hash (`tier:{t}:n`), largest first. */
function tiers(fields: Fields): string[] {
  return Object.keys(fields)
    .filter((k) => k.startsWith('tier:') && k.endsWith(':n'))
    .map((k) => k.slice(5, -2))
    .filter((t) => t.length > 0 && !t.includes(':'))
    .sort((a, b) => lowerBound(b) - lowerBound(a) || a.localeCompare(b))
}

/** A share needs a whole: with none (absent or 0) it cannot be stated. */
const share = (part: number | undefined, whole: number | undefined): ReactNode =>
  whole ? pct((100 * (part ?? 0)) / whole) : <Nil />

/** Proportion rows: name, a neutral bar, the count and its share. */
function Mix({ rows, whole }: { rows: Array<[string, number]>; whole: number | undefined }) {
  return (
    <div className="dist sc-dist">
      {rows.map(([k, v]) => (
        <div className="dist-r" key={k}>
          <span title={k}>{k}</span>
          {/* no whole, no bar: a 0-width bar would draw a share of nothing */}
          {whole ? <Bar pct={(100 * v) / whole} state="idle" /> : <span />}
          <span className="dist-n tnum">{n(v)}</span>
          <span className="dist-p tnum q">{share(v, whole)}</span>
        </div>
      ))}
    </div>
  )
}

/** The top age bucket is open (`181+`). */
const AGE_OPEN_FROM_D = 180

/** The median data age. The collector interpolates it from the five age
 *  buckets, so under the open bucket it is an ESTIMATE (good to a few weeks) and
 *  reads "≈ N d". Past 180 d the median sits in the open `181+` bucket, whose
 *  interpolated number is made up: the board states only what is known,
 *  "> 180 d", and carries no figure the bucket cannot support — on hover either.
 *
 *  A `data_stale` finding marks the LABEL, never the figure: the collector judges
 *  its own window (today, or today + yesterday) while this median covers the
 *  selected range, so lighting the figure would name a number the finding never
 *  looked at. */
function MedianAge({ days, sev }: { days: number; sev: Alarm | null }) {
  const open = days > AGE_OPEN_FROM_D
  return (
    <Stat label={<span className={lit(sev)}><Mark sev={sev} />median data age</span>} unit=" d"
          value={open ? `> ${AGE_OPEN_FROM_D}` : `≈ ${days.toFixed(1)}`}
          title={open
            ? `above ${AGE_OPEN_FROM_D} d: the open 181+ bucket has no upper bound, so no point estimate`
            : `≈ ${days.toFixed(1)} d, estimated from the age buckets`} />
  )
}

/** Outcomes over the payload's own window: what was scored, how it was judged
 *  (bands, gates, per tier), how old the data behind it was, and the
 *  anomalies the server raised. */
export function ScoringOutcomes() {
  const q = useScoringOutcomes()
  const o = q.data
  const t: Fields = o?.totals ?? {}
  const total = t['n']
  // THE PAYLOAD'S window, not the board range: while a new range loads,
  // placeholderData keeps the previous payload on screen, and only the payload
  // knows which window its figures cover.
  const win = o ? `last ${o.window_days} day${o.window_days === 1 ? '' : 's'} (UTC)` : ''
  const fs = o?.findings ?? []
  const state = { isLoading: q.isLoading, error: q.error }
  const none = <p className="panel-load"><Nil>{CANNOT}</Nil> — nothing recorded in the {win}</p>

  return (
    <>
      <Tile title="Scored per day" hint={o ? <>{win} · <AsOf iso={o.generated_at} /></> : undefined}>
        <PanelState {...state}>
          {o ? (
            <>
              <DayBars label={`channels scored per day, ${win}`} unit="scored"
                       days={[...o.days].reverse().map((d) => ({ day: d.day, n: d.fields['n'] ?? null }))} />
              {total === undefined ? none : (
                <>
                  <p className="note tnum">
                    <b className="sc-strong">{n(total)}</b> scored in the {win}
                    {t['score_sum'] !== undefined && total ? ` · mean score, all channels ${(t['score_sum'] / total).toFixed(1)}` : ''}
                    {family(t, 'version:').map(([v, c]) => ` · ${versionWord(v)} ${n(c)}`).join('')}
                  </p>
                  {family(t, 'capped:').length ? (
                    <>
                      <p className="sc-sub" style={{ marginTop: 12 }}>capped share</p>
                      <Mix rows={family(t, 'capped:').map(([k, v]) => [CAPPED[k] ?? k, v])} whole={total} />
                    </>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </PanelState>
      </Tile>

      {/* A score_shift finding marks the tier it NAMES: it judges that tier's ungated
          mean over today (UTC), or today + yesterday early in the day, against the
          7 days before — not the window's ungated mean shown here. */}
      <Tile title="Per tier"
            hint={o ? `${win} · a score_shift finding marks its tier — it judges ${JUDGED}, not this window` : undefined}>
        <PanelState {...state}>
          {o ? (tiers(t).length === 0 ? none : (
            <div style={{ overflowX: 'auto' }}>
              <table className="t sc-t">
                <thead>
                  <tr>
                    <th>tier</th><th className="n">n</th>
                    <th className="n" title="mean score of the tier's channels that no hard gate forced to 0 — Insufficient Data, No Long-Form and Dormant score 0 and are left out">ungated mean</th>
                    <th className="n">% Avoid</th><th className="n">% Insufficient Data</th><th className="n">% Dormant</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers(t).map((tier) => {
                    const tn = t[`tier:${tier}:n`]
                    // Only the three HARD gates force a score to 0, so they add nothing
                    // to score_sum and are left out of the count it is divided by.
                    // Blocked, Not Family Safe and Data Fetch Error keep their score
                    // and stay in. A gate field the payload lacks counts as 0.
                    const sum = t[`tier:${tier}:score_sum`]
                    const hard = HARD_GATES.reduce((a, g) => a + (t[`tier:${tier}:gate:${g}`] ?? 0), 0)
                    const ungated = tn === undefined ? 0 : tn - hard
                    const mean = sum === undefined || ungated <= 0 ? null : sum / ungated
                    // the finding names the tier (a stated string), so the mark is
                    // on the tier's name; the ungated mean beside it is the window's.
                    const shift = sevOf(fs, `score_shift.${tier}`)
                    return (
                      <tr key={tier}>
                        <td className={lit(shift)}><Mark sev={shift} />{tier}</td>
                        <td className="n">{n(tn ?? null)}</td>
                        <td className="n">{mean === null ? <Nil /> : mean.toFixed(1)}</td>
                        <td className="n q">{share(t[`tier:${tier}:band:Avoid`], tn)}</td>
                        <td className="n q">{share(t[`tier:${tier}:gate:Insufficient Data`], tn)}</td>
                        <td className="n q">{share(t[`tier:${tier}:gate:Dormant`], tn)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )) : null}
        </PanelState>
      </Tile>

      <Tile title="Band & gate mix" hint={o ? win : undefined}>
        <PanelState {...state}>
          {o ? (total === undefined ? none : (
            <>
              <p className="sc-sub">band</p>
              <Mix rows={ordered(family(t, 'band:'), BANDS)} whole={total} />
              <p className="sc-sub" style={{ marginTop: 14 }}>gate</p>
              <Mix rows={ordered(family(t, 'gate:'), GATES)} whole={total} />
            </>
          )) : null}
        </PanelState>
      </Tile>

      <Tile title="Data age"
            hint={o ? `${win} · snapshot age at scoring · a data_stale finding judges ${JUDGED}` : undefined}>
        <PanelState {...state}>
          {o ? (
            <>
              <div className="stats-row" style={{ marginBottom: 12 }}>
                {o.median_age_days === null
                  ? <Stat label="median data age" value={<Nil />} sub={<Nil>{CANNOT}</Nil>} />
                  : <MedianAge days={o.median_age_days} sev={sevOf(fs, 'data_stale')} />}
              </div>
              {family(t, 'age:').length === 0 ? none : (
                <Mix whole={t['age_n']}
                     rows={family(t, 'age:')
                       .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
                       .map(([k, v]) => [`${k.replace('-', '–')} d`, v])} />
              )}
            </>
          ) : null}
        </PanelState>
      </Tile>

      {/* LAST, and always placed: anomalies arriving must not push the
          outcome tiles down the page, and "nothing raised" is itself a
          reading, so the tile states what the collector would judge. */}
      <Tile title="Outcome anomalies" span hint={`judged on ${JUDGED}, once ≥ 500 channels are scored`}>
        <PanelState {...state}>
          {o ? (fs.length
            ? <Findings label="outcome anomalies" items={fs} />
            : <p className="note" style={{ marginTop: 0 }}>
                No anomaly reported — the collector judges {JUDGED} once ≥ 500 channels are scored.
              </p>) : null}
        </PanelState>
      </Tile>
    </>
  )
}

/* ------------------------------------------------------------- 5. failures */

const RECENT_ROWS = 25

/** Failures: which channels keep failing (re-queued forever — the poison
 *  finding), the commonest error texts, and the ring itself, newest first. */
export function ScoringFailures() {
  const q = useScoringFailures()
  const f = q.data
  const state = { isLoading: q.isLoading, error: q.error }
  const genS = f ? Date.parse(f.generated_at) / 1000 : NaN
  const poison = sevOf(f?.findings ?? [], 'poison_channels')
  return (
    <>
      <Tile title="Repeated channels" hint="3+ per-channel failures in the ring · whole-batch outages excluded">
        <PanelState {...state}>
          {f ? (
            <>
              <Findings label="failure findings" items={f.findings} />
              {f.repeated.length === 0 ? (
                <p className="panel-load">no channel failed 3 or more times in the ring</p>
              ) : (
                <table className="t sc-t" style={{ marginTop: f.findings.length ? 12 : 0 }}>
                  <thead>
                    <tr><th>channel</th><th className="n">failures</th><th className="n">last</th></tr>
                  </thead>
                  <tbody>
                    {f.repeated.map((r) => (
                      <tr key={r.channelid}>
                        <td className="sc-flag">{r.channelid}</td>
                        <td className={`n ${lit(poison)}`}><Mark sev={poison} />{n(r.count)}</td>
                        <td className="n q">
                          {r.last_at === null || Number.isNaN(genS) ? <Nil /> : age(Math.max(0, genS - r.last_at))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : null}
        </PanelState>
      </Tile>

      <Tile title="Top error texts" hint="first 80 characters, grouped">
        <PanelState {...state}>
          {f ? (f.by_error.length === 0 ? <p className="panel-load">no error text in the ring</p> : (
            <table className="t sc-t">
              <thead><tr><th>error</th><th className="n">count</th></tr></thead>
              <tbody>
                {f.by_error.map((e, i) => (
                  <tr key={`${i}:${e.error}`}>
                    <td><span className="sc-clip" title={e.error}>{e.error}</span></td>
                    <td className="n">{n(e.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )) : null}
        </PanelState>
      </Tile>

      <Tile title="Recent failures" span
            hint={f ? <>{n(f.total)} in the ring (newest 100)
              {Object.entries(f.kinds).map(([k, c]) => ` · ${n(c)} ${k}`).join('')}
              {' · '}<AsOf iso={f.generated_at} /></> : undefined}>
        <PanelState {...state}>
          {f ? (
            <>
              {f.malformed > 0 ? (
                <p className="note" style={{ marginTop: 0 }}>{n(f.malformed)} ring entries were not readable and are not counted.</p>
              ) : null}
              {f.items.length === 0 ? <p className="panel-load">no failures in the ring</p> : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="t sc-t">
                    <thead>
                      <tr><th className="n">age</th><th>kind</th><th>channel</th><th>error</th></tr>
                    </thead>
                    <tbody>
                      {f.items.slice(0, RECENT_ROWS).map((it, i) => (
                        <tr key={i}>
                          <td className="n q">{it.age_s === null ? <Nil /> : age(it.age_s)}</td>
                          <td className="q">{it.kind}</td>
                          {/* A whole-batch outage names no channel — a stated fact,
                              not a figure the server could not state. */}
                          <td>{it.channelid ?? (it.kind === 'batch' ? 'whole batch' : <Nil>not named</Nil>)}</td>
                          <td><span className="sc-clip" title={it.error}>{it.error}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {f.items.length > RECENT_ROWS ? (
                    <p className="note">and {n(f.items.length - RECENT_ROWS)} older in the ring</p>
                  ) : null}
                </div>
              )}
            </>
          ) : null}
        </PanelState>
      </Tile>
    </>
  )
}

/* ---------------------------------------------------------- 6. API & jobs */

const short = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id)
const cnt = (v: number | null) => (v === null ? <Nil /> : n(v))

function JobRow({ j }: { j: ScoringJob }) {
  return (
    <tr>
      <td className="q" title={j.job_id}>{short(j.job_id)}</td>
      {/* A job's state is a WORD, in the neutral register, whatever it is. The
          alarm for a failed job is job_failed's (warn, last 24 h), listed in the
          same tile: a hue here would raise one for a failure from last week that
          nothing judges, and COMPLETED is not a health claim (a bulk job can
          complete with failed channels). */}
      <td><Pill state="idle">{j.status}</Pill></td>
      <td className="q">{j.input_type ?? <Nil />}</td>
      <td className="n">{cnt(j.total)}</td>
      <td className="n">{cnt(j.processed)}</td>
      <td className="n">{cnt(j.failed_channels)}</td>
      <td className="n q">{j.age_s === null ? <Nil /> : age(j.age_s)}</td>
      <td>{j.error_message ? <span className="sc-clip" title={j.error_message}>{j.error_message}</span> : ''}</td>
    </tr>
  )
}

/** API & jobs: the scoring API's health probe, the Celery backlog, and the
 *  CSV jobs with the job findings. Two tiles, two queries, two failure states. */
export function ScoringApiJobs() {
  const ov = useScoringOverview()
  const jq = useScoringJobs()
  const o = ov.data
  const j: JobsPayload | undefined = jq.data
  const fs = o?.findings ?? []
  const apiSev = sevOf(fs, 'api_down')
  const backlogSev = sevOf(fs, 'celery_backlog')
  return (
    <>
      {/* Spans the row, like the jobs table under it: alone in a two-column row
          it would strand the right half of the grid. */}
      <Tile title="Scoring API" span hint={o ? <AsOf iso={o.generated_at} /> : undefined}>
        <PanelState isLoading={ov.isLoading} error={ov.error}>
          {o ? (
            <>
              <div className="stats-row">
                <Stat label="health probe"
                      value={o.api.ok === null ? <Nil /> : <><Mark sev={apiSev} />{o.api.ok ? 'up' : 'down'}</>}
                      title={o.api.ok === null ? undefined : o.api.ok ? 'up' : 'down'}
                      state={apiSev ?? undefined}
                      sub={o.api.error ?? (
                        <>{o.api.status === null ? <Nil /> : `HTTP ${o.api.status}`}
                          {' · '}{o.api.latency_ms === null ? <Nil /> : `${o.api.latency_ms}ms`}</>
                      )} />
                <Stat label="Celery backlog"
                      value={o.celery_backlog === null ? <Nil />
                        : <><Mark sev={backlogSev} />{n(o.celery_backlog)}</>}
                      title={o.celery_backlog === null ? undefined : n(o.celery_backlog)}
                      state={backlogSev ?? undefined}
                      sub={o.celery_backlog === null ? <Nil>{CANNOT}</Nil> : 'tasks waiting'} />
                <Stat label="score version"
                      value={o.info.score_version === null ? <Nil /> : versionWord(o.info.score_version)}
                      sub={o.info.rapidapi_enabled === null ? <Nil>RapidAPI {CANNOT}</Nil>
                        : `RapidAPI ${o.info.rapidapi_enabled ? 'on' : 'off'}`} />
              </div>
              <Findings label="API findings" items={fs.filter((f) => API_FINDINGS.includes(f.id))} />
            </>
          ) : null}
        </PanelState>
      </Tile>

      <Tile title="CSV scoring jobs" span
            hint={j ? <>last {j.limit} of channel_score_jobs · <AsOf iso={j.generated_at} /></> : undefined}>
        <PanelState isLoading={jq.isLoading} error={jq.error}>
          {j ? (
            <>
              <p className="note tnum" style={{ marginTop: 0 }}>
                {Object.entries(j.counts).map(([s, c]) => `${n(c)} ${s.toLowerCase()}`).join(' · ')}
                {' '}— whole table
              </p>
              <Findings label="job findings" items={j.findings} />
              {j.jobs.length === 0 ? <p className="panel-load">no CSV scoring job on record</p> : (
                <div style={{ overflowX: 'auto', marginTop: 10 }}>
                  <table className="t sc-t">
                    <thead>
                      <tr>
                        <th>job</th><th>state</th><th>input</th><th className="n">total</th>
                        <th className="n">processed</th><th className="n">failed</th>
                        <th className="n">idle for</th><th>error</th>
                      </tr>
                    </thead>
                    <tbody>{j.jobs.map((x) => <JobRow key={x.job_id} j={x} />)}</tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </PanelState>
      </Tile>
    </>
  )
}

/* ---------------------------------------------------------------- the rail */

/**
 * The Scoring rail entry, as a pure function of the four finding-bearing reads.
 *
 * Value: channels scored TODAY — resets daily, so it falls to nothing when
 * scoring stops (the vidIQ lesson: a lifetime total reads normal through an
 * outage). A null from a successful read is "cannot state", not "unavailable".
 *
 * Dot: the worst crit/warn across the findings of all four endpoints, so the
 * rail can never show green beside a scoring warning in Triage; `info` never
 * raises it. `idle` only when /overview itself says idle and nothing else
 * raised anything. Blind is not healthy: a failed /overview read is crit (as
 * every rail entry treats its primary source); a failed secondary read is warn
 * — the severity the collector itself gives an unreadable source
 * (`source_unreadable.*`) — and its stale findings are not reused.
 */
export function scoringRail(
  overview: Read<ScoringOverview>, outcomes: Read<OutcomesPayload>,
  jobs: Read<JobsPayload>, failures: Read<FailuresPayload>,
): RailEntry {
  const base = { id: 'scoring', name: 'Scoring', repo: 'yt-channel-scoring', unit: 'scored · today' } as const
  if (overview.isError) {
    return { ...base, value: null, state: 'crit', note: '/metrics/scoring could not be read' }
  }
  const o = overview.data
  const reads = scoringReads(overview, outcomes, jobs, failures)
  const findings = reads.flatMap((r) => r.findings)
  const unread = reads.filter((r) => r.unread).map((r) => `${r.path} could not be read`)
  const crit = findings.find((f) => f.sev === 'crit')
  const warn = findings.find((f) => f.sev === 'warn')

  const state: RailEntry['state'] =
    crit || o?.state === 'crit' ? 'crit'
      : warn || unread.length || o?.state === 'warn' ? 'warn'
        : !o || o.state === 'idle' ? 'idle'
          : 'ok'
  const note = state === 'crit' ? crit?.title
    : state === 'warn' ? warn?.title ?? unread[0]
      : undefined

  return {
    ...base,
    value: o ? o.today.scored : undefined,
    unstated: o !== undefined && o.today.scored === null,
    state,
    note,
  }
}

/** The rail entry, from the same cached hooks the panels use — no extra read. */
export function useScoringRail(): RailEntry {
  return scoringRail(useScoringOverview(), useScoringOutcomes(), useScoringJobs(), useScoringFailures())
}
